/**
 * Pure derivation helpers for the WBR callout summary section
 * (edit-callout-summary spec, Requirements 12 & 13).
 *
 * The Summary_Banner and the Key_Callouts_Table are *derived* (read-only) from
 * the unified `wbr_callouts` records the viewer already loads via `getCallouts`.
 * They are NOT part of the editable Narrative. Keeping the derivation in pure
 * functions lets the viewer recompute both parts from the same callout list it
 * uses to render the per-callout cards, so they stay consistent and recompute
 * whenever callouts are added, edited, or deleted (Req 12.6, 13.3).
 */

import type { Callout } from "../../models/API";

/**
 * A callout as consumed by the derivation helpers. The backend `getCallouts`
 * query already filters `deleted_at IS NULL`, so the frontend `Callout` type
 * carries no `deleted_at`. We accept an optional `deleted_at` here as defense
 * in depth so a row that does carry a non-null `deleted_at` is still excluded
 * from the banner counts (Req 12.3) and the key-callouts table (Req 13.1, 13.4).
 */
export type DerivableCallout = Callout & { deleted_at?: string | null };

/**
 * The four-cell Summary_Banner counts. The "Evidence Coverage" cell was removed
 * (Req 12.2) and replaced with a "Manual Callouts" cell that counts how many of
 * the displayed callouts were created manually (`source === 'manual'`) vs.
 * AI-generated (`source === 'ai'`).
 *
 * - `total`   → "Total Callouts" cell value
 * - `positive`→ "Positive Signals" cell value (`status === 'positive'`)
 * - `watch`   → "Positive Signals" cell sub-text (`status === 'watch'`)
 * - `large`   → "Large Callouts" cell value (`impact === 'large'`)
 * - `medium`  → "Large Callouts" cell sub-text (`impact === 'medium'`)
 * - `small`   → "Large Callouts" cell sub-text (`impact === 'small'`)
 * - `manual`  → "Manual Callouts" cell value (`source === 'manual'`)
 * - `ai`      → "Manual Callouts" cell sub-text (`source === 'ai'`)
 */
export interface SummaryBannerCounts {
  total: number;
  positive: number;
  watch: number;
  large: number;
  medium: number;
  small: number;
  manual: number;
  ai: number;
}

/**
 * One derived Key_Callouts_Table row. Each row references the specific callout
 * record it represents by its callout `id` (Req 13.2) and carries the display
 * fields needed to render the row without re-reading the source list.
 */
export interface KeyCalloutRow {
  id: number;
  title: string;
  status: string;
  trend?: string | null;
  impact?: string | null;
  why_text?: string | null;
  card_number: number;
  /** The source callout record this row was derived from. */
  callout: DerivableCallout;
}

/** Optional (team_id, week, report_name) scope for the displayed report. */
export interface SummaryTupleScope {
  team_id: number;
  week: string;
  report_name: string;
}

/** A row is deleted when it carries a non-empty `deleted_at` value. */
function isDeleted(c: DerivableCallout): boolean {
  return (
    c.deleted_at !== undefined &&
    c.deleted_at !== null &&
    String(c.deleted_at).trim() !== ""
  );
}

/**
 * A No_Callout_Placeholder has an empty / whitespace-only title. Such rows are
 * never persisted as real callouts, but we exclude them defensively so a stray
 * placeholder cannot inflate the banner counts (Decision #10, Req 12.3, 12.8).
 */
function isPlaceholder(c: DerivableCallout): boolean {
  return !c.title || c.title.trim() === "";
}

/**
 * When a scope is provided, keep only callouts belonging to the displayed
 * (team_id, week, report_name) tuple (Req 12.3, 13.1, 13.7). When omitted the
 * caller is responsible for passing an already-scoped list.
 */
function matchesScope(c: DerivableCallout, scope?: SummaryTupleScope): boolean {
  if (!scope) return true;
  return (
    c.team_id === scope.team_id &&
    c.week === scope.week &&
    c.report_name === scope.report_name
  );
}

/**
 * Sort comparator for callouts by their visible card number (ascending).
 * Callouts without an assigned number (`card_number <= 0`) sink to the bottom;
 * ties break on DB id for stability.
 *
 * Mirrors the comparator in `WBRCalloutsViewer` so the Key_Callouts_Table order
 * matches the numbered badges on the per-callout cards (Req 13 ordering policy).
 */
export function compareByCardNumber(
  a: DerivableCallout,
  b: DerivableCallout
): number {
  const an =
    a.card_number && a.card_number > 0
      ? a.card_number
      : Number.MAX_SAFE_INTEGER;
  const bn =
    b.card_number && b.card_number > 0
      ? b.card_number
      : Number.MAX_SAFE_INTEGER;
  if (an !== bn) return an - bn;
  return a.id - b.id;
}

/**
 * Derive the read-only three-cell Summary_Banner counts from the callout list
 * (Req 12.3–12.5, 12.8).
 *
 * The "counted set" is every row for the displayed tuple where `deleted_at IS
 * NULL` and the row is not a No_Callout_Placeholder (non-empty trimmed title).
 * All counts are `0` when the counted set is empty (Req 12.8). The "Manual
 * Callouts" cell counts rows with `source === 'manual'`; its sub-text counts
 * AI-generated rows (`source === 'ai'`).
 *
 * @param callouts the unified callout list (typically already report-scoped)
 * @param scope    optional (team_id, week, report_name) to scope the count
 */
export function deriveBanner(
  callouts: ReadonlyArray<DerivableCallout> | null | undefined,
  scope?: SummaryTupleScope
): SummaryBannerCounts {
  const counts: SummaryBannerCounts = {
    total: 0,
    positive: 0,
    watch: 0,
    large: 0,
    medium: 0,
    small: 0,
    manual: 0,
    ai: 0,
  };

  if (!callouts) return counts;

  for (const c of callouts) {
    if (isDeleted(c)) continue;
    if (isPlaceholder(c)) continue;
    if (!matchesScope(c, scope)) continue;

    counts.total += 1;

    if (c.status === "positive") counts.positive += 1;
    else if (c.status === "watch") counts.watch += 1;

    if (c.impact === "large") counts.large += 1;
    else if (c.impact === "medium") counts.medium += 1;
    else if (c.impact === "small") counts.small += 1;

    if (c.source === "manual") counts.manual += 1;
    else if (c.source === "ai") counts.ai += 1;
  }

  return counts;
}

/**
 * Derive the Key_Callouts_Table rows from the callout list (Req 13.1, 13.2,
 * 13.4, 13.7).
 *
 * Rows are scoped to the displayed tuple (when `scope` is supplied), exclude
 * deleted rows (`deleted_at IS NULL`), carry the source callout `id`, and are
 * ordered with the same `compareByCardNumber` policy the viewer uses for the
 * per-callout cards. Unlike the banner, placeholder rows are not specially
 * excluded here beyond the deleted filter — the spec scopes the table by
 * `deleted_at IS NULL` and tuple membership only.
 *
 * @param callouts the unified callout list (typically already report-scoped)
 * @param scope    optional (team_id, week, report_name) to scope the rows
 */
export function deriveKeyCallouts(
  callouts: ReadonlyArray<DerivableCallout> | null | undefined,
  scope?: SummaryTupleScope
): KeyCalloutRow[] {
  if (!callouts) return [];

  return callouts
    .filter((c) => !isDeleted(c) && matchesScope(c, scope))
    .slice()
    .sort(compareByCardNumber)
    .slice(0, 3)
    .map((c) => ({
      id: c.id,
      title: c.title,
      status: c.status,
      trend: c.trend,
      impact: c.impact,
      why_text: c.why_text,
      card_number: c.card_number,
      callout: c,
    }));
}
